import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, UserCheck } from "lucide-react";
import { getRestaurantNews, NewsItem, NewsCategory, LinkedEstablishment } from "../../interfaces/restaurantNews";
import { getMyProspects } from "../../interfaces/prospect";
import { getMyInfo } from "../../interfaces/user";
import { isAdminUser } from "../../utils/admin";
import { ProspectionEmailModal } from "../../components/prospection/ProspectionEmailModal";
import MultiCityAutocomplete from "../../components/fields/MultiCityAutocomplete";
import { FRENCH_DEPARTMENTS, FRENCH_REGIONS, REGION_TO_DEPARTMENTS } from "../../lib/frenchCities";
import { useEdgeAlignedDropdown } from "../../hooks/useEdgeAlignedDropdown";
import { isTrialUser, trialVisibleCount } from "../../utils/trial";
import { NewsBoard } from "./ChrNewsBoard";

// ── ChrNewsTab ────────────────────────────────────────────────────────────────
// The /admin/restaurant-news/feed endpoint read as a prospecting worklist. Shown in the
// dashboard's "Actualité CHR" tab and in the "Signaux presse" folder (/signals/folder/press-signals).
// The tab is a prospecting list: openings, changes of owner and hiring are reasons to call;
// closings are not, so they are left out ("Tout" = these three).
const NEWS_FILTERS: Array<{ id: NewsCategory; label: string }> = [
    { id: "all", label: "Tout" },
    { id: "opening", label: "Ouvertures" },
    { id: "ownership", label: "Changements de propriétaire" },
    { id: "recruitment", label: "Recrutements" },
];
const NEWS_TAB_CATEGORIES = NEWS_FILTERS.filter((f) => f.id !== "all").map((f) => f.id);
const NEWS_PAGE_SIZE = 12;

export default function ChrNewsTab() {
    const [category, setCategory] = useState<NewsCategory>("all");
    const [cityFilters, setCityFilters] = useState<string[]>([]);
    const [departmentFilters, setDepartmentFilters] = useState<string[]>([]);
    const [regionFilters, setRegionFilters] = useState<string[]>([]);
    const [items, setItems] = useState<NewsItem[] | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [emailModalItem, setEmailModalItem] = useState<NewsItem | null>(null);
    const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // Establishments already in the rep's CRM, so « Ajouter au CRM » reads « Dans le CRM » there.
    const [inCrm, setInCrm] = useState<Set<string>>(new Set());
    // Read off /user/me, like the sidebar — gates the « Booster l'enrichissement » button.
    const [isAdmin, setIsAdmin] = useState(false);
    // Guards re-entry while a page is in flight (a ref, not state — the IntersectionObserver
    // callback fires faster than a re-render).
    const loadingMoreRef = useRef(false);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    // Trial users get the first page only, a third of it readable.
    const isTrial = isTrialUser();
    const visibleCount = trialVisibleCount(items?.length ?? 0, isTrial);

    // Same convention as RestaurantDatabase: région is never sent as its own param — it's
    // expanded client-side into département codes and merged with any explicit picks.
    const departmentsParam = (() => {
        const regionDepts = regionFilters.flatMap((r) => REGION_TO_DEPARTMENTS[r] ?? []);
        const allDepts = [...new Set([...departmentFilters, ...regionDepts])];
        return allDepts.length > 0 ? allDepts.join(",") : undefined;
    })();
    const cityParam = cityFilters.length > 0 ? cityFilters.join(",") : undefined;
    // On by default: an article is only worth a call when we know who runs the place.
    const [onlyWithOwner, setOnlyWithOwner] = useState(true);
    const hasOwnerParam = onlyWithOwner ? ("true" as const) : undefined;
    const categoryParam = category === "all" ? NEWS_TAB_CATEGORIES.join(",") : category;

    useEffect(() => {
        setItems(null);
        loadingMoreRef.current = false;
        getRestaurantNews({ category: categoryParam, city: cityParam, departments: departmentsParam, hasOwner: hasOwnerParam, limit: NEWS_PAGE_SIZE, page: 1 })
            .then((d) => {
                setItems(d.items || []);
                setPage(1);
                setTotalPages(d.totalPages || 1);
                setCategoryCounts(d.categoryCounts || {});
                setSelectedId(d.items?.[0]?._id ?? null);
            })
            .catch((err) => {
                console.error("CHR news tab failed:", err);
                setItems([]);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category, cityParam, departmentsParam, hasOwnerParam]);

    useEffect(() => {
        getMyProspects()
            .then((res) => {
                const list: any[] = Array.isArray(res) ? res : res?.data || [];
                setInCrm(new Set(list.map((p) => p?.establishmentId).filter(Boolean).map(String)));
            })
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        getMyInfo()
            .then((res) => setIsAdmin(isAdminUser(res?.data)))
            .catch(() => undefined);
    }, []);

    // A boost repaints every article on that establishment. The rep's own LinkedIn invitation
    // stamp is not in the boost's copy, so it is carried over from the sheet it replaces.
    const updateEstablishment = useCallback((next: LinkedEstablishment) => {
        setItems((prev) =>
            prev
                ? prev.map((it) => {
                      const cur = it.linkedEstablishment;
                      if (!cur || cur._id !== next._id) return it;
                      const inviteOf = new Map((cur.owners ?? []).map((o) => [o._id, o.linkedinInvite]));
                      return {
                          ...it,
                          linkedEstablishment: {
                              ...next,
                              owners: (next.owners ?? []).map((o) => ({ ...o, linkedinInvite: inviteOf.get(o._id) ?? o.linkedinInvite ?? null })),
                          },
                      };
                  })
                : prev
        );
    }, []);

    const hasMore = page < totalPages;

    const loadMore = useCallback(() => {
        if (loadingMoreRef.current || page >= totalPages) return;
        const nextPage = page + 1;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        getRestaurantNews({ category: categoryParam, city: cityParam, departments: departmentsParam, hasOwner: hasOwnerParam, limit: NEWS_PAGE_SIZE, page: nextPage })
            .then((d) => {
                // Dedupe by _id — a filter change or a StrictMode double-run must never
                // stack the same article twice.
                setItems((prev) => {
                    const seen = new Set((prev ?? []).map((i) => i._id));
                    return [...(prev ?? []), ...(d.items || []).filter((i) => !seen.has(i._id))];
                });
                setPage(nextPage);
                if (d.totalPages) setTotalPages(d.totalPages);
            })
            .catch((err) => console.error("CHR news load more failed:", err))
            .finally(() => {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            });
    }, [page, totalPages, categoryParam, cityParam, departmentsParam, hasOwnerParam]);

    // Infinite scroll — auto-load the next page as the sentinel nears the viewport (600px
    // ahead, so the spinner is brief and content keeps flowing). The manual button below is
    // kept as a fallback / affordance. A filter change unmounts the list (skeleton) and mounts a
    // new sentinel, so the observer is re-attached whenever the list comes back.
    const listShown = items !== null;
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !hasMore || typeof IntersectionObserver === "undefined") return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMore();
            },
            { rootMargin: "600px 0px" }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [hasMore, loadMore, listShown]);

    // Dropdown open state + edge-aware alignment, same pattern as the database's toolbar.
    const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
    const [showRegionDropdown, setShowRegionDropdown] = useState(false);
    const [departmentSearch, setDepartmentSearch] = useState("");
    const [regionSearch, setRegionSearch] = useState("");
    const departmentDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showDepartmentDropdown);
    const regionDropdownAlign = useEdgeAlignedDropdown<HTMLDivElement>(showRegionDropdown);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest(".relative")) {
                setShowDepartmentDropdown(false);
                setShowRegionDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <>
        <div>
            {/* One toolbar: the category as a segmented control, then where and who — every control
                h-8 with the database's border style. */}
            <div className="flex items-center gap-2 flex-wrap mb-4">
                <div role="group" aria-label="Type d'actualité" className="inline-flex h-8 max-w-full items-center overflow-x-auto rounded-md border border-border bg-surface p-0.5">
                    {NEWS_FILTERS.map(({ id, label }) => {
                        const count = id === "all"
                            ? NEWS_TAB_CATEGORIES.reduce((sum, c) => sum + (categoryCounts[c] ?? 0), 0)
                            : categoryCounts[id] ?? 0;
                        const on = category === id;
                        return (
                            <button
                                key={id}
                                onClick={() => setCategory(id)}
                                aria-pressed={on}
                                className={`inline-flex h-full shrink-0 items-center gap-1.5 whitespace-nowrap rounded px-2.5 font-sans text-xs font-medium
                                    transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                                        on ? "bg-ink text-ink-inverse" : "text-ink-muted hover:text-ink"
                                    }`}
                            >
                                {label}
                                <span className={`tabular-nums font-normal ${on ? "text-ink-inverse/60" : "text-ink-faint"}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>

                <span aria-hidden className="mx-1 hidden h-5 w-px bg-border sm:block" />

                <MultiCityAutocomplete values={cityFilters} onChange={setCityFilters} />

                <div className="relative">
                    <button
                        onClick={() => {
                            setShowDepartmentDropdown(!showDepartmentDropdown);
                            setShowRegionDropdown(false);
                        }}
                        className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                            departmentFilters.length > 0
                                ? "bg-ink text-ink-inverse border border-ink hover:bg-ink/90"
                                : "bg-surface text-ink-muted border border-border hover:border-border-strong"
                        }`}
                    >
                        <span>Département</span>
                        {departmentFilters.length > 0 && (
                            <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                {departmentFilters.length}
                            </span>
                        )}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {showDepartmentDropdown && (
                        <div ref={departmentDropdownAlign.ref} className={`absolute top-full mt-1 ${departmentDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-56 flex flex-col`} style={{ maxHeight: 280 }}>
                            <div className="px-2 py-1.5 border-b border-border shrink-0">
                                <input
                                    type="text"
                                    value={departmentSearch}
                                    onChange={(e) => setDepartmentSearch(e.target.value)}
                                    placeholder="Rechercher un département..."
                                    className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div className="overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                {FRENCH_DEPARTMENTS.filter((d) => {
                                    const q = departmentSearch.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                                    const name = d.name.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                                    return !q || d.code.toLowerCase().startsWith(q) || name.includes(q);
                                }).map((dep) => (
                                    <label key={dep.code} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={departmentFilters.includes(dep.code)}
                                            onChange={(e) => {
                                                setDepartmentFilters(e.target.checked
                                                    ? [...departmentFilters, dep.code]
                                                    : departmentFilters.filter((c) => c !== dep.code)
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                        />
                                        <span className="text-xs text-ink-muted">{dep.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="relative">
                    <button
                        onClick={() => {
                            setShowRegionDropdown(!showRegionDropdown);
                            setShowDepartmentDropdown(false);
                        }}
                        className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                            regionFilters.length > 0
                                ? "bg-ink text-ink-inverse border border-ink hover:bg-ink/90"
                                : "bg-surface text-ink-muted border border-border hover:border-border-strong"
                        }`}
                    >
                        <span>Région</span>
                        {regionFilters.length > 0 && (
                            <span className="px-1 py-0.5 bg-ink-inverse/20 text-ink-inverse rounded-sm text-[10px] font-bold">
                                {regionFilters.length}
                            </span>
                        )}
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {showRegionDropdown && (
                        <div ref={regionDropdownAlign.ref} className={`absolute top-full mt-1 ${regionDropdownAlign.alignClass} bg-surface border border-border rounded-2xl shadow-float z-50 w-64 flex flex-col`} style={{ maxHeight: 280 }}>
                            <div className="px-2 py-1.5 border-b border-border shrink-0">
                                <input
                                    type="text"
                                    value={regionSearch}
                                    onChange={(e) => setRegionSearch(e.target.value)}
                                    placeholder="Rechercher une région..."
                                    className="w-full text-xs px-2 py-1 rounded-lg border border-border focus:outline-none focus:border-accent"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                            <div className="overflow-y-auto overflow-x-hidden ds-scrollbar py-1">
                                {FRENCH_REGIONS.filter((r) => {
                                    const q = regionSearch.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                                    const name = r.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
                                    return !q || name.includes(q);
                                }).map((region) => (
                                    <label key={region} className="flex items-center gap-2 px-3 py-1.5 hover:bg-surface-sunken cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={regionFilters.includes(region)}
                                            onChange={(e) => {
                                                setRegionFilters(e.target.checked
                                                    ? [...regionFilters, region]
                                                    : regionFilters.filter((r2) => r2 !== region)
                                                );
                                            }}
                                            className="w-3.5 h-3.5 rounded-sm border-border accent-accent"
                                        />
                                        <span className="text-xs text-ink-muted">{region}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Gérant identifié — only articles whose establishment has a known gérant, so
                    every row has someone to call. */}
                <button
                    onClick={() => setOnlyWithOwner((v) => !v)}
                    aria-pressed={onlyWithOwner}
                    className={`h-8 px-2.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                        onlyWithOwner
                            ? "bg-ink text-ink-inverse border border-ink hover:bg-ink/90"
                            : "bg-surface text-ink-muted border border-border hover:border-border-strong"
                    }`}
                >
                    <UserCheck className="w-3.5 h-3.5" />
                    <span>Gérant identifié</span>
                </button>
            </div>

            {items === null ? (
                <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
                    <div className="rounded-2xl border border-border bg-surface overflow-hidden motion-safe:animate-pulse">
                        <div className="h-10 border-b border-border" />
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex gap-4 border-b border-border last:border-b-0 px-5 py-5">
                                <div className="h-6 w-20 rounded-full bg-surface-sunken" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-3.5 w-1/3 rounded-sm bg-surface-sunken" />
                                    <div className="h-3 w-1/4 rounded-sm bg-surface-sunken" />
                                    <div className="h-3 w-2/3 rounded-sm bg-surface-sunken" />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="hidden xl:block h-[520px] rounded-2xl border border-border bg-surface motion-safe:animate-pulse" />
                </div>
            ) : items.length > 0 ? (
                <NewsBoard
                    items={items}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                    visibleCount={visibleCount}
                    isTrial={isTrial}
                    inCrm={inCrm}
                    onAddedToCrm={(id) => setInCrm((prev) => new Set(prev).add(id))}
                    onEmail={setEmailModalItem}
                    isAdmin={isAdmin}
                    onEstablishmentUpdated={updateEstablishment}
                    footer={
                        <>
                            {hasMore && !isTrial && (
                                <div ref={sentinelRef} className="flex justify-center border-t border-border py-4">
                                    {loadingMore ? (
                                        <span className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium text-ink-faint">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Chargement…
                                        </span>
                                    ) : (
                                        <button
                                            onClick={loadMore}
                                            className="rounded-full bg-surface-sunken text-ink-muted hover:text-ink px-4 py-2 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        >
                                            Charger plus
                                        </button>
                                    )}
                                </div>
                            )}
                            {!hasMore && items.length > NEWS_PAGE_SIZE && (
                                <p className="border-t border-border py-4 text-center text-[11px] text-ink-faint">Fin des actualités</p>
                            )}
                        </>
                    }
                />
            ) : (
                <div className="py-12 text-center">
                    <p className="text-xs text-ink-faint">Aucune actualité pour cette catégorie</p>
                </div>
            )}
        </div>

        {emailModalItem && emailModalItem.linkedEstablishment && (
            <ProspectionEmailModal
                articleId={emailModalItem._id}
                establishmentName={emailModalItem.linkedEstablishment.name}
                onClose={() => setEmailModalItem(null)}
            />
        )}
        </>
    );
}
